import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CovToastService, IconService} from '@cov/ui-angular';
import {translate, TranslocoService} from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
  timer,
} from 'rxjs';
import {MaintenanceService} from '../services/maintenance.service';
import {UrlService} from '../services/url.service';

@Injectable({
  providedIn: 'root',
})
export class ResponseInterceptor implements HttpInterceptor {
  // INJECTS
  private toastService = inject(CovToastService);
  private maintenanceService = inject(MaintenanceService);
  private urlService = inject(UrlService);
  private transloco = inject(TranslocoService);
  private iconService = inject(IconService);
  private translocoLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private toastQueue: CallableFunction[] = [];

  constructor() {
    combineLatest([
      this.transloco.events$.pipe(
        filter((event) => event.type === 'langChanged'),
        take(1),
      ),
      this.iconService.isInitialized$.pipe(
        filter((isInitialized) => isInitialized),
        take(1),
      ),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.translocoLoaded.next(true);
        this.toastQueue.forEach((callable) => callable());
        this.toastQueue = [];
      });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          !request.url.startsWith(this.urlService.rawUrl()) &&
          !request.url.startsWith(this.urlService.binaryUrl())
        ) {
          return throwError(() => error);
        }

        const errorHandlers: Record<number, () => Observable<HttpEvent<any>> | void> = {
          0: () => this.handleServiceUnavailable(next, request) as Observable<HttpEvent<any>>,
          401: () => throwError(() => error),
          403: () => this.handleForbidden(error),
          404: () => throwError(() => error), // No toast for 404
          500: () => this.handleResponseError(error, 'global.http500'),
          503: () => this.handleServiceUnavailable(next, request) as Observable<HttpEvent<any>>,
          504: () => this.handleResponseError(error, 'global.http504'),
        };

        return (
          errorHandlers[error.status]?.() ?? this.handleResponseError(error, 'global.httpError')
        );
      }),
    );
  }

  private handleForbidden(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }

  private handleRetry(
    next: HttpHandler,
    request: HttpRequest<any>,
    timeoutInSeconds: number,
  ): Observable<any> {
    return timer(timeoutInSeconds * 1000).pipe(switchMap(() => next.handle(request)));
  }

  private handleServiceUnavailable(next: HttpHandler, request: HttpRequest<any>) {
    return this.handleRetry(next, request, 3).pipe(
      catchError((error) => {
        if (error.status === 503 || error.status === 0) {
          return this.maintenanceService.checkMaintenance().pipe(
            take(1),
            switchMap(() => {
              return throwError(() => error);
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private handleResponseError(
    error: HttpErrorResponse,
    defaultErrorMessage: string,
  ): Observable<never> {
    const errorMessages = error?.error?.errors;
    const errorMessageString = errorMessages?.[0]?.message;

    if (errorMessageString) {
      this.handleToast(() => this.toastService.error(errorMessageString));
    } else if (errorMessages?.length) {
      errorMessages.forEach(({reason}: {reason: string}) =>
        this.handleToast(() => this.toastService.error(translate(reason))),
      );
    } else {
      this.handleToast(() => this.toastService.error(translate(defaultErrorMessage)));
    }
    return throwError(() => error);
  }

  private handleToast(callable: CallableFunction): void {
    this.translocoLoaded.getValue() ? callable() : this.toastQueue.push(callable);
  }
}
